import {
  FilterOperator,
  type SearchAvailableExamCenterProctorsByTimeFrameInput,
  type CreateExamEventInput,
  type GetAvailableRoomsByLocationIdAndTimeFrameInput,
  ExamCenterProctorType,
  OrderType,
} from '@exam-center/graphql';
import { dayjs } from '@shared/utils';

import { type ExamEventCreationFormType } from './schema';
import { getExamTime } from './utils';

function convertToStartOfMinute(date: Date): Date {
  return dayjs(date).startOf('minute').toDate();
}

function convertToISODate(date: Date): Date {
  return new Date(date.toISOString());
}

function setToExamDate(time: Date, examDate: Date): Date {
  return dayjs(examDate)
    .set('hours', time.getHours())
    .set('minutes', time.getMinutes())
    .set('seconds', time.getSeconds())
    .toDate();
}

function mapSessions(
  sessions: ExamEventCreationFormType['sessions'],
  examStartTime: Date,
  examEndTime: Date,
) {
  const examStartTimeISO = convertToISODate(examStartTime);
  const examEndTimeISO = convertToISODate(examEndTime);

  if (!sessions || sessions.length === 0) {
    return [
      {
        round: 1,
        startedAt: examStartTimeISO,
        endedAt: examEndTimeISO,
      },
    ];
  }

  return sessions.map((session, index) => {
    const startedAt = session.startedAt
      ? convertToISODate(convertToStartOfMinute(setToExamDate(session.startedAt, examStartTime)))
      : examStartTimeISO;

    const endedAt = session.endedAt
      ? convertToISODate(convertToStartOfMinute(setToExamDate(session.endedAt, examStartTime)))
      : examEndTimeISO;

    return {
      round: index + 1,
      startedAt,
      endedAt,
    };
  });
}

export function mapExamEventCreationFormInputsToExamEventCreationInput(
  data: ExamEventCreationFormType,
  examCenterCode: string,
  examCoursesOptions: { label: string; value: string }[],
): CreateExamEventInput {
  if (
    !data.examEventName ||
    !data.examType ||
    !data.registrationType ||
    !data.eventStartDate ||
    !data.eventEndDate ||
    !data.examDate.date ||
    !data.examDate.startTime ||
    !data.examDate.endTime ||
    !data.location?.province ||
    !data.location?.locationName ||
    !data.location?.room ||
    !data.location?.seat
  ) {
    throw new Error('Invalid exam event creation form data');
  }

  const { examStartTime, examEndTime } = getExamTime(
    data.examDate.date,
    data.examDate.startTime,
    data.examDate.endTime,
  );

  const examStartedAt = convertToISODate(examStartTime);
  const examEndedAt = convertToISODate(examEndTime);

  const registrationStartedAt = convertToISODate(convertToStartOfMinute(data.eventStartDate));
  const registrationEndedAt = convertToISODate(convertToStartOfMinute(data.eventEndDate));

  const sessions = mapSessions(data.sessions, examStartTime, examEndTime);

  const trimmedGroupCode = data.groupCode?.trim();
  const formattedGroupCode = !trimmedGroupCode?.length ? undefined : trimmedGroupCode;

  const examCourseValues = data.examCourses?.map((course) => course.value).filter(Boolean) ?? [];

  const isAllSelected = examCourseValues.includes('all');

  const examCourseIds = isAllSelected
    ? examCoursesOptions.map((course) => course.value)
    : examCourseValues;

  return {
    examCenterCode,
    examStartedAt,
    examEndedAt,
    examType: data.examType,
    registrationType: data.registrationType,
    examLocationId: data.location.locationName.value ?? '',
    name: data.examEventName,
    proctorIds: data.mainProctors?.map((proctor) => proctor.value) ?? [],
    registrationStartedAt,
    registrationEndedAt,
    examCourseIds,
    seat: data.location.seat,
    roomIds: Object.entries(data.location.room ?? {})
      .filter(([, value]) => value)
      .map(([id]) => id),
    sessions,
    metadata: {
      applicantGroup: {
        code: formattedGroupCode,
        type: data.groupType?.value,
      },
    },
  };
}

export function mapExamEventCreationFormInputsToGetAvailableRoomsInput(
  data: ExamEventCreationFormType,
  examCenterCode: string,
): GetAvailableRoomsByLocationIdAndTimeFrameInput {
  const { location, examDate } = data;

  if (
    !location?.locationName?.value ||
    !examDate?.date ||
    !examDate?.startTime ||
    !examDate?.endTime
  ) {
    throw new Error('Invalid exam event creation form data for room availability');
  }

  const locationId = location.locationName.value;
  const { examStartTime, examEndTime } = getExamTime(
    examDate.date,
    examDate.startTime,
    examDate.endTime,
  );

  return {
    locationId,
    startedAt: examStartTime,
    endedAt: examEndTime,
    examCenterCode,
  };
}

export function mapExamEventCreationFormInputsToGetAvailableProctorsInput(
  data: ExamEventCreationFormType,
  examCenterCode: string,
): SearchAvailableExamCenterProctorsByTimeFrameInput {
  const { examDate } = data;

  if (!examDate?.date || !examDate?.startTime || !examDate?.endTime) {
    throw new Error('Invalid exam event creation form data for proctor availability');
  }

  const { examStartTime, examEndTime } = getExamTime(
    examDate.date,
    examDate.startTime,
    examDate.endTime,
  );

  return {
    startedAt: examStartTime,
    endedAt: examEndTime,
    searchOptions: {
      filter: {
        conditions: [
          {
            field: 'examCenterCode',
            operator: FilterOperator.Equal,
            value: examCenterCode,
          },
          {
            field: 'type',
            operator: FilterOperator.Equal,
            value: ExamCenterProctorType.Proctor_1,
          },
        ],
      },
      paging: {
        pageSize: 200,
        currentPage: 0,
      },
      order: {
        type: OrderType.Asc,
        field: 'firstNameTh',
      },
    },
  };
}

export const __test__ = {
  convertToStartOfMinute,
  convertToISODate,
  setToExamDate,
  mapSessions,
};
